import React from 'react';

import {
  Field,
  Grid,
  Margin,
  Select,
  SelectItem,
  Spacer,
  Text,
  TextInput,
} from '@zapier/design-system';

import AsyncButton from 'app/developer-v3/components/AsyncButton';
import Link from 'app/common/components/Link';
import { BASE_DOC_URL_VB } from 'app/developer-v3/constants';
import {
  PUBLISHING_FORM_FIELD_BLANK_VALUES as FIELD_BLANK_VALUES,
  useForm,
} from 'app/developer-v3/hooks';

import Step from './Step';
import { useStepCompletion } from './useStepCompletion';
import { getError, parseSaveErrors } from './utils';

import type { AppDetailsValues as Values } from './types';

type Props = {
  initialValues: Values;
  isDisabled: boolean;
  onComplete: (isComplete: boolean) => void;
  onSubmit: (valuesNext: Values) => Promise<void>;
};

export default function AppDetails({
  initialValues,
  isDisabled,
  onComplete,
  onSubmit,
}: Props) {
  const form = useForm<Values>({
    enableReinitialize: true,
    initialValues,
    onSubmit: async formValues => {
      try {
        await onSubmit(formValues);
      } catch (errors) {
        Object.entries(parseSaveErrors(errors)).forEach(([key, value]) => {
          form.setFieldError(key, value as string);
        });
      }
    },
    validate: (values: Values) => {
      const errors: Partial<Record<keyof Values, string>> = {};
      if (values.hasBeenLaunched === FIELD_BLANK_VALUES.hasBeenLaunched) {
        errors.hasBeenLaunched = 'Required.';
      }
      if (
        values.isReplacementIntegration ===
        FIELD_BLANK_VALUES.isReplacementIntegration
      ) {
        errors.isReplacementIntegration = 'Required.';
      }
      if (
        values.alreadyManagingSimilarIntegration ===
        FIELD_BLANK_VALUES.alreadyManagingSimilarIntegration
      ) {
        errors.alreadyManagingSimilarIntegration = 'Required.';
      }
      if (
        values.isReplacementIntegration ||
        values.alreadyManagingSimilarIntegration
      ) {
        if (
          values.linkToOtherIntegrations ===
          FIELD_BLANK_VALUES.linkToOtherIntegrations
        ) {
          errors.linkToOtherIntegrations = 'Required.';
        }
        if (
          values.newIntegrationRationale ===
          FIELD_BLANK_VALUES.newIntegrationRationale
        ) {
          errors.newIntegrationRationale = 'Required.';
        }
      }
      if (values.homepageUrl === FIELD_BLANK_VALUES.homepageUrl) {
        errors.homepageUrl = 'Homepage URL is required.';
      }
      if (values.apiDocsUrl === FIELD_BLANK_VALUES.apiDocsUrl) {
        errors.apiDocsUrl = 'API documentation URL is required.';
      }
      if (values.primaryColor === FIELD_BLANK_VALUES.primaryColor) {
        errors.primaryColor = 'Primary color is required.';
      }
      return errors;
    },
    validateOnMount: true,
  });

  const isTouched = Object.values(form.isTouched).some(value => value);

  const isCompleted = !form.isDirty && form.isValid;
  useStepCompletion(isCompleted, onComplete);

  return (
    <Step
      isComplete={!form.isDirty && form.isValid}
      isError={isTouched && !form.isValid}
      title="App details"
    >
      <Grid columns="1" gap="20px">
        <Select
          errorMessage={getError(
            'hasBeenLaunched',
            form.isTouched,
            form.errors
          )}
          isDisabled={isDisabled}
          isRequired={true}
          label="Application has been publicly launched"
          helpText={`We need confirmation that your application has been fully launched to the public and meets the publishing requirement.`}
          isInvalid={
            form.isTouched.hasBeenLaunched && !!form.errors.hasBeenLaunched
          }
          onBlur={() => form.setFieldTouched('hasBeenLaunched', true)}
          onChange={key =>
            form.setFieldValue('hasBeenLaunched', key === 'true')
          }
          selectedKey={
            form.values.hasBeenLaunched === true
              ? 'true'
              : form.values.hasBeenLaunched === false
              ? 'false'
              : null
          }
        >
          <SelectItem key="true" value="true">
            Yes
          </SelectItem>
          <SelectItem key="false" value="false">
            No
          </SelectItem>
        </Select>
        <Select
          errorMessage={getError(
            'isReplacementIntegration',
            form.isTouched,
            form.errors
          )}
          isDisabled={isDisabled}
          isRequired={true}
          label="Is this integration a replacement for an existing one?"
          helpText="Review the publishing requirement."
          isInvalid={
            form.isTouched.isReplacementIntegration &&
            !!form.errors.isReplacementIntegration
          }
          onBlur={() => form.setFieldTouched('isReplacementIntegration', true)}
          onChange={key =>
            form.setFieldValue('isReplacementIntegration', key === 'true')
          }
          selectedKey={
            form.values.isReplacementIntegration === true
              ? 'true'
              : form.values.isReplacementIntegration === false
              ? 'false'
              : null
          }
        >
          <SelectItem key="true" value="true">
            Yes
          </SelectItem>
          <SelectItem key="false" value="false">
            No
          </SelectItem>
        </Select>
        <Select
          errorMessage={getError(
            'alreadyManagingSimilarIntegration',
            form.isTouched,
            form.errors
          )}
          isDisabled={isDisabled}
          isRequired={true}
          label="Are you already managing a public Zapier integration using the same API/authentication?"
          isInvalid={
            form.isTouched.alreadyManagingSimilarIntegration &&
            !!form.errors.alreadyManagingSimilarIntegration
          }
          onBlur={() =>
            form.setFieldTouched('alreadyManagingSimilarIntegration', true)
          }
          onChange={key =>
            form.setFieldValue(
              'alreadyManagingSimilarIntegration',
              key === 'true'
            )
          }
          selectedKey={
            form.values.alreadyManagingSimilarIntegration === true
              ? 'true'
              : form.values.alreadyManagingSimilarIntegration === false
              ? 'false'
              : null
          }
        >
          <SelectItem key="true" value="true">
            Yes
          </SelectItem>
          <SelectItem key="false" value="false">
            No
          </SelectItem>
        </Select>
        {(form.values.isReplacementIntegration === true ||
          form.values.alreadyManagingSimilarIntegration === true) && (
          <Margin margin="0px 0px 0px 20px">
            <Grid columns="1" gap="20px">
              <Field
                error={getError(
                  'linkToOtherIntegrations',
                  form.isTouched,
                  form.errors
                )}
                isDisabled={isDisabled}
                isRequired={true}
                label="Link to the other integration(s)"
                renderInput={inputProps => (
                  <TextInput
                    {...inputProps}
                    aria-label="Link to the other integration(s)"
                    name="linkToOtherIntegrations"
                    onBlur={form.onBlur}
                    onChange={form.onChange}
                    value={form.values.linkToOtherIntegrations}
                  />
                )}
              />
              <Field
                error={getError(
                  'newIntegrationRationale',
                  form.isTouched,
                  form.errors
                )}
                isDisabled={isDisabled}
                isRequired={true}
                label="Share why you're creating a new integration"
                renderHelpText={() => (
                  <Text color={isDisabled ? 'GrayWarm5' : undefined} tag="p">
                    Please reference our publishing requirements{' '}
                    <Link
                      href={`${BASE_DOC_URL_VB}/publish/integration-publishing-requirements#2-1-replacement-integrations`}
                      target="_blank"
                    >
                      here
                    </Link>
                    . Each published integration should represent a distinct,
                    independent app to ensure the best experience for our mutual
                    customers. Do not create separate integrations for
                    functionality that share the same authentication method and
                    interact with the same API, unless each integration is for a
                    separate product or service (not feature) that is clearly
                    marketed independently.
                  </Text>
                )}
                renderInput={inputProps => (
                  <TextInput
                    {...inputProps}
                    aria-label="Share why you're creating a new integration"
                    name="newIntegrationRationale"
                    onBlur={form.onBlur}
                    onChange={form.onChange}
                    value={form.values.newIntegrationRationale}
                  />
                )}
              />
            </Grid>
          </Margin>
        )}
        <Field
          error={getError('homepageUrl', form.isTouched, form.errors)}
          isDisabled={isDisabled}
          isRequired={true}
          label="Homepage URL (e.g., https://zapier.com)"
          renderHelpText={() => (
            <Text color={isDisabled ? 'GrayWarm5' : undefined} tag="p">
              This should be the URL of the application's marketing website, not
              the application URL or login page, etc.
            </Text>
          )}
          renderInput={inputProps => (
            <TextInput
              {...inputProps}
              aria-label="Homepage URL (e.g., https://zapier.com)"
              name="homepageUrl"
              onBlur={form.onBlur}
              onChange={form.onChange}
              value={form.values.homepageUrl}
            />
          )}
        />
        <Field
          error={getError('apiDocsUrl', form.isTouched, form.errors)}
          isDisabled={isDisabled}
          isRequired={true}
          label="API documentation URL"
          renderHelpText={() => (
            <Text color={isDisabled ? 'GrayWarm5' : undefined} tag="p">
              We require clear, comprehensive API documentation covering all API
              endpoints to meet this{' '}
              <Link
                href={`${BASE_DOC_URL_VB}/publish/integration-publishing-requirements#5-2-app-apis-are-documented`}
                target="_blank"
              >
                publishing requirement
              </Link>
              . This allows us to support users effectively. It can be public,
              behind a login, or shared via fixed credentials, but OpenAPI docs
              are highly recommended.
            </Text>
          )}
          renderInput={inputProps => (
            <TextInput
              {...inputProps}
              aria-label="API documentation URL"
              name="apiDocsUrl"
              onBlur={form.onBlur}
              onChange={form.onChange}
              value={form.values.apiDocsUrl}
            />
          )}
        />
        <Field
          error={getError('apiDocumentationNotes', form.isTouched, form.errors)}
          isDisabled={isDisabled}
          isRequired={false}
          label="API documentation notes"
          renderHelpText={() => (
            <Text color={isDisabled ? 'GrayWarm5' : undefined} tag="p">
              If you need to share extra details (e.g. access instructions), use
              this field to do so.
            </Text>
          )}
          renderInput={inputProps => (
            <TextInput
              {...inputProps}
              aria-label="API documentation notes"
              name="apiDocumentationNotes"
              onBlur={form.onBlur}
              onChange={form.onChange}
              value={form.values.apiDocumentationNotes}
            />
          )}
        />
        <Field
          error={getError('primaryColor', form.isTouched, form.errors)}
          isDisabled={isDisabled}
          isRequired={true}
          label="Primary color (HEX, e.g., #FF4A00)"
          renderInput={inputProps => (
            <TextInput
              {...inputProps}
              aria-label="Primary color (HEX, e.g., #FF4A00)"
              name="primaryColor"
              onBlur={form.onBlur}
              onChange={form.onChange}
              type="text"
              value={form.values.primaryColor}
            />
          )}
        />
        <Field
          error={getError('twitterUsername', form.isTouched, form.errors)}
          isDisabled={isDisabled}
          isRequired={false}
          label="Twitter username"
          renderInput={inputProps => (
            <TextInput
              {...inputProps}
              aria-label="Twitter username"
              name="twitterUsername"
              onBlur={form.onBlur}
              onChange={form.onChange}
              value={form.values.twitterUsername}
            />
          )}
        />
      </Grid>
      <Spacer height={20} />
      <AsyncButton
        aria-label="Save app details"
        variant="secondary"
        isDisabled={
          isDisabled || !form.isDirty || !form.isValid || form.isSubmitting
        }
        isWorking={form.isSubmitting}
        onPress={() => form.onSubmit()}
        type="button"
      >
        Save
      </AsyncButton>
    </Step>
  );
}
